import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	TemplateRef,
	ViewChild,
	WritableSignal,
	computed,
	signal
} from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { arrayHelper } from 'lib/helpers/array-helper.class.js';
import { cloneHelper } from 'lib/helpers/clone-helper.class.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { IAzureFoodSearchItem } from 'lib/search/search.models.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { CardImage, CardPieGraph } from 'lib/ui/card/index.js';
import { DialogFormFieldType, IDialogButtonsContext, SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { ListItem } from 'lib/ui/list/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { Subject, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { IViewOrEditDietPlanRouteData } from 'src/app/routes/trainer/diet-routes.js';
import { foodHelper } from '../foods/food.helper.js';

type EditTextDialog = {
	readonly text?: string;
};

interface IExtendedDietFood {
	originalDietFood: DietDb.IDietFood;
	name: string;
	amountText: string;
	linkedFood: FoodDb.IFoodItem | undefined;
	cardImages: CardImage[];
	category?: string;
	buttons: IButton[];
	pieGraph?: CardPieGraph;
	nutrition: FoodDb.IFoodNutrients;
	portionSelectionItems: SelectItemField<FoodDb.IFoodPortion>[];
	foodAvatar: IImage | undefined;
	notes: string | undefined;
}

interface IExtendedDietFoodContainer {
	originalContainer: DietDb.IDietFoodContainer;
	domId: string;
	shownTitle: WritableSignal<string | undefined>;
	shownSubtitle: string | undefined;
	foods: IExtendedDietFood[];
	outsideContainerButtons: IButton[];
	insideContainerButtons: IButton[];
	mealTypeButtons: IButton[];
	mealNutrition: FoodDb.IFoodNutrients;
}

interface IDietSaveConfig {
	scrollToContainerId?: string;
}

type AddMealLocation = 'top' | 'bottom';

@Component({
	templateUrl: './view-or-edit-diet.component.html',
	selector: 'mod-view-or-edit-diet',
	styleUrls: ['view-or-edit-diet.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ViewOrEditDietComponent extends TypeCoreComponent<IViewOrEditDietPlanRouteData> implements OnInit {
	@ViewChild('newFoodTemplate', { static: true }) newFoodTemplate!: TemplateRef<any>;

	private readonly _saveDiet = new Subject<IDietSaveConfig | undefined>();

	private readonly gramsPortion: FoodDb.IFoodPortion = foodHelper.getGramsBasedFoodPortion();
	private readonly customFoods = signal<readonly FoodDb.IFoodItem[]>([]);
	private readonly fetchedFoods = signal<readonly FoodDb.IFoodItem[]>([]);
	private readonly lastOpenedSelectFoodDialog = signal<DietDb.IDietFoodContainer | undefined>(undefined);

	public readonly titleMaxLength: number = 50;
	public readonly amountMaxValue: number = 10000;

	public readonly isEdit = signal<boolean>(false);

	public readonly enablePreview = computed<boolean>(() => {
		const authUser = this.authUser();

		if (authUser?.userType === 'trainer') {
			return true;
		}

		return false;
	});

	public readonly editNotesButton = computed<IButton | undefined>(() => {
		if (!this.isEdit()) {
			return undefined;
		}

		const diet = this.diet();

		if (!diet) {
			return undefined;
		}

		return {
			icon: 'edit',
			iconOnly: true,
			title: 'Edit notes',
			click: () => {
				this.openEditDialog({
					diet,
					fieldType: 'textarea',
					getProp: (diet) => diet.notes,
					setProp: (diet, value) => {
						diet.notes = value;
					},
					title: 'Notes'
				});
			}
		};
	});

	public readonly diet = signal<DietDb.IDietItem | undefined>(undefined);
	public readonly dietNutrition = computed<FoodDb.IFoodNutrients | undefined>(() => {
		const combinedLinkedItems = this.combinedLinkedItems();
		const diet = this.diet();

		if (!diet) {
			return undefined;
		}

		return foodHelper.calculateFoodNutrientsForDiet(
			diet,
			linkedItemsHelper.getLinkedItemsByType<FoodDb.IFoodItem>(combinedLinkedItems, 'Food')
		);
	});

	public readonly extendedDietFoodContainers = computed<readonly IExtendedDietFoodContainer[] | undefined>(() => {
		const diet = this.diet();

		if (!diet) {
			return undefined;
		}

		return this.getExtendedDietContainers({
			diet: diet,
			linkedItems: this.combinedLinkedItems()
		});
	});

	private readonly combinedLinkedItems = computed<CosmosDb.ICosmosItem[]>(() => {
		return [...(this.sharedData()?.linkedItems ?? []), ...this.customFoods(), ...this.fetchedFoods()];
	});

	public readonly addMealToTopButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();
		const foodContainers = this.extendedDietFoodContainers();

		if (!sharedData || !foodContainers) {
			return [];
		}

		const buttons: IButton[] = [];

		buttons.push({
			buttonSize: 'full',
			title: foodContainers.length === 0 ? `Add meal` : `Add meal to top`,
			icon: foodContainers.length === 0 ? 'add' : 'add',
			buttonType: 'primary',
			click: () => {
				this.handleAddMeal('top');
			}
		});

		return buttons;
	});

	public readonly addMealToBottomButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();
		const foodContainers = this.extendedDietFoodContainers();

		if (!sharedData || !foodContainers) {
			return [];
		}

		if (foodContainers.length === 0) {
			return [];
		}

		const buttons: IButton[] = [];

		buttons.push({
			buttonSize: 'full',
			title: `Add meal to bottom`,
			icon: 'add',
			buttonType: 'primary',
			click: () => {
				this.handleAddMeal('bottom');
			}
		});

		return buttons;
	});

	public readonly editQuickActionButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData || !this.isEdit()) {
			return [];
		}

		const buttons: IButton[] = [
			...this.addMealToTopButtons(),
			{
				title: 'Create food',
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'add',
				click: () => {
					this.openCreateCustomFoodDialog();
				}
			}
		];
		const dietMealsCount = this.diet()?.foodContainers?.length ?? 0;

		if (dietMealsCount > 0) {
			buttons.push({
				title: `Remove all meals`,
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'remove',
				confirm: {
					title: 'Remove meals',
					text: `Are you sure to remove all (${dietMealsCount}) meals from diet?`,
					confirmMode: 'default'
				},
				click: () => {
					this.removeAllMeals();
				}
			});
		}

		return buttons;
	});

	constructor() {
		super();

		this.subscribeToDietSave();

		this.dataInit = () => {
			const authUser = this.authUser();

			if (!authUser) {
				return;
			}

			this.isEdit.set(authUser.userType === 'client' ? false : true);
		};

		this.sharedDataInit = (sharedData) => {
			if (this.isEdit()) {
				this.initFoods();
			}

			this.diet.set(sharedData?.data?.diet);
		};
	}

	handleCreateFood(food: FoodDb.IFoodItem): void {
		this.customFoods.update((oldValue) => {
			return [...oldValue, food];
		});

		this.dependencies.dialogService.closeAllDialogs();

		const lastSelectedContainer = this.lastOpenedSelectFoodDialog();

		if (lastSelectedContainer) {
			this.handleAddFood(food, lastSelectedContainer);
		}
	}

	handleSetTitle(container: IExtendedDietFoodContainer, title?: string): void {
		container.originalContainer.title = title ?? '';
		this.updateDiet();
	}

	handleRemoveContainer(container: DietDb.IDietFoodContainer): void {
		const diet = this.diet();
		if (!diet) {
			return;
		}

		this.dependencies.dialogService.openConfirmationDialog({
			mode: 'delete',
			text: 'Are you sure to remove this diet meal including all foods?',
			confirm: () => {
				diet.foodContainers = diet.foodContainers.filter((m) => m.id !== container.id);
				this.updateDiet();
			}
		});
	}

	handleClickFoodItem(container: IExtendedDietFoodContainer, foodItem: IExtendedDietFood): void {
		if (this.isMobileView()) {
			// do not handle click on mobiles
			return;
		}

		// first button should be the edit button
		foodItem.buttons[0]?.click?.();
	}

	handleAddMeal(location: AddMealLocation): void {
		const diet = this.diet();
		if (!diet) {
			return;
		}

		const containers: DietDb.IDietFoodContainer[] = diet.foodContainers ?? [];
		const containerId = guidHelper.shortGuid();
		const newMeal: DietDb.IDietFoodContainer = {
			id: containerId,
			title: `Meal`,
			foods: []
		};

		if (location === 'top') {
			containers.unshift(newMeal);
		} else {
			containers.push(newMeal);
		}

		this.updateDiet({
			scrollToContainerId: containerId
		});
	}

	private removeAllMeals(): void {
		const diet = this.diet();
		if (!diet) {
			return;
		}

		diet.foodContainers = [];
		this.updateDiet();
	}

	private openSelectFoodDialog(container: DietDb.IDietFoodContainer): void {
		this.lastOpenedSelectFoodDialog.set(container);
		this.dependencies.dialogService.openDialogWithSections({
			dialogId: 'openSelectFoodDialog',
			size: 'default',
			sections: [
				{
					id: 'global',
					name: 'Global foods',
					dialog: (builder) => {
						return builder.azureSearchDialog<IAzureFoodSearchItem>({
							title: `Select food`,
							buttons: (context) => this.getSelectFoodDialogButtons(context),
							search: (data) => this.dependencies.azureSearchService.searchFoodItems(data),
							mapSearchResponse: (response) =>
								response.items.map((m) => {
									const graph = foodHelper.getFoodSearchItemNutritionGraphConfig(m);
									const listItem: ListItem<IAzureFoodSearchItem> = {
										data: m,
										id: m.id,
										title: m.name,
										subtitle: m.category,
										pieGraph: {
											singleSeries: graph.singleSeries,
											colorScheme: graph.colorScheme
										}
									};

									return listItem;
								}),
							select: (item) => {
								super.subscribeToObservable(
									this.dependencies.httpService.getSingle<FoodDb.IFoodItem>('food', item.id).pipe(
										map((response) => {
											// add food to linked items so that it's available
											this.fetchedFoods.update((foods) => [...foods, response.data]);
											this.handleAddFood(response.data, container);
										})
									)
								);
							}
						});
					}
				},
				{
					id: 'custom',
					name: 'Custom foods',
					dialog: (builder) => {
						return builder.selectionDialog<FoodDb.IFoodItem>({
							title: `Select food`,
							buttons: (context) => this.getSelectFoodDialogButtons(context),
							items: this.customFoods().map((food) => {
								const selectionItem: SelectionDialogItem<FoodDb.IFoodItem> = {
									data: food,
									id: food.id,
									title: food.system.name,
									subtitle: food.category
								};

								return selectionItem;
							}),
							select: (item) => {
								this.handleAddFood(item, container);
							}
						});
					}
				}
			]
		});
	}

	private updateDiet(saveConfig?: IDietSaveConfig): void {
		const diet = this.diet();

		if (diet) {
			this.diet.set(cloneHelper.clone(diet));
			this._saveDiet.next(saveConfig);
		}
	}

	private handleEditAmount(
		container: DietDb.IDietFoodContainer,
		food: FoodDb.IFoodItem,
		portionSelectionItems: SelectItemField<FoodDb.IFoodPortion>[],
		dietFood: DietDb.IDietFood
	): void {
		this.dependencies.dialogService.openFoodAmountDialog({
			title: `${food.system.name}`,
			subtitle: `Set amount for food`,
			foodAmount: dietFood.foodAmount,
			amountMaxValue: this.amountMaxValue,
			portionSelectionItems: portionSelectionItems,
			food: food,
			save: (foodAmount) => {
				dietFood.foodAmount = foodAmount;
				this.updateDiet();
			}
		});
	}

	private handleRemoveFood(container: DietDb.IDietFoodContainer, food: DietDb.IDietFood): void {
		container.foods = container.foods.filter((m) => m.id !== food.id);
		this.updateDiet();
	}

	private canMoveDown(diet: DietDb.IDietItem, index: number): boolean {
		if (!diet.foodContainers.length) {
			return false;
		}

		if (index === diet.foodContainers.length - 1) {
			return false;
		}

		return true;
	}

	private canMoveUp(diet: DietDb.IDietItem, index: number): boolean {
		if (!diet.foodContainers.length) {
			return false;
		}

		if (index <= 0) {
			return false;
		}

		return true;
	}

	private handleMove(diet: DietDb.IDietItem, container: DietDb.IDietFoodContainer, direction: 'up' | 'down'): void {
		const containers = diet.foodContainers;
		const index = containers.findIndex((m) => m.id === container.id);

		if (direction === 'up') {
			if (!this.canMoveUp(diet, index)) {
				return;
			}

			const previousIndex: number = index;
			const newIndex: number = index - 1;

			arrayHelper.moveItemInArray(containers, previousIndex, newIndex);
		} else if (direction === 'down') {
			if (!this.canMoveDown(diet, index)) {
				return;
			}

			const previousIndex: number = index;
			const newIndex: number = index + 1;

			arrayHelper.moveItemInArray(containers, previousIndex, newIndex);
		} else {
			throw Error(`Invalid direction '${direction}'`);
		}

		this.updateDiet();
	}

	private openCreateCustomFoodDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create food',
			subtitle: 'You may create as many custom foods as you need',
			size: 'large',
			template: () => this.newFoodTemplate
		});
	}

	private getSelectFoodDialogButtons(context: IDialogButtonsContext): IButton[] {
		return [
			{
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'add',
				title: 'Create food',
				click: () => {
					context.closeDialog();
					this.openCreateCustomFoodDialog();
				}
			}
		];
	}

	private handleAddFood(food: FoodDb.IFoodItem, container: DietDb.IDietFoodContainer): void {
		const newFoodId: string = guidHelper.shortGuid();

		container.foods.push({
			id: newFoodId,
			foodId: food.id,
			foodAmount: {
				amount: this.gramsPortion.amount,
				gramsPerAmount: this.gramsPortion.gramWeight,
				portionId: this.gramsPortion.id
			}
		});

		this.updateDiet();

		// open edit food container
		const updatedContainer = this.extendedDietFoodContainers()?.find(
			(m) => m.originalContainer.id === container.id
		);

		if (updatedContainer) {
			const updatedFoodItem = updatedContainer.foods.find((m) => m.originalDietFood.id === newFoodId);

			if (updatedFoodItem) {
				// edit should be the first button
				updatedFoodItem.buttons[0]?.click?.();
			}
		}
	}

	private subscribeToDietSave(): void {
		super.subscribeToObservable(
			this._saveDiet.pipe(
				observableHelper.saveDebounceTime({ isMobileView: this.isMobileView() }),
				map((config) => {
					this.saveDiet(config);
				})
			)
		);
	}

	private getExtendedDietContainers(data: {
		readonly diet: DietDb.IDietItem;
		readonly linkedItems: readonly CosmosDb.ICosmosItem[];
	}): readonly IExtendedDietFoodContainer[] {
		let containerIndex: number = 0;
		const containers = data.diet.foodContainers;

		return containers.map((container) => {
			let foodIndex: number = 0;

			const extendedFoods: IExtendedDietFood[] = container.foods.map((food) => {
				const linkedFood = super.getOptionalItem<FoodDb.IFoodItem>(food.foodId, data.linkedItems);

				const sourcePortions: FoodDb.IFoodPortion[] = [foodHelper.getGramsBasedFoodPortion()];

				let pieGraph: CardPieGraph | undefined = undefined;

				if (linkedFood) {
					const graphConfig = foodHelper.getNutritionGraphConfig(linkedFood.nutrients, { per100g: false });
					pieGraph = {
						singleSeries: graphConfig.singleSeries,
						colorScheme: graphConfig.colorScheme
					};

					sourcePortions.push(...(linkedFood.portions ?? []));
				}

				const portionSelectionItems: SelectItemField<FoodDb.IFoodPortion>[] = sourcePortions.map((portion) => {
					const item: SelectItemField<FoodDb.IFoodPortion> = {
						data: portion,
						title: portion.name,
						value: portion.id
					};

					return item;
				});

				const buttons: IButton[] = [];

				if (linkedFood) {
					buttons.push({
						iconOnly: true,
						title: `Edit amount`,
						tooltip: `Edit amount`,
						buttonSize: 'default',
						icon: 'edit',
						buttonType: 'basic',
						click: (event) => {
							event?.stopPropagation();
							event?.preventDefault();
							this.handleEditAmount(container, linkedFood, portionSelectionItems, food);
						}
					});
				}

				buttons.push({
					iconOnly: true,
					title: `Remove food`,
					tooltip: `Remove food`,
					buttonSize: 'default',
					icon: 'remove',
					buttonType: 'basic',
					click: (event) => {
						event?.stopPropagation();
						event?.preventDefault();
						this.handleRemoveFood(container, food);
					}
				});

				const extendedFood: IExtendedDietFood = {
					originalDietFood: food,
					nutrition: foodHelper.calculateFoodNutrientsForAmount(linkedFood, food.foodAmount),
					buttons: buttons,
					notes: linkedFood?.notes,
					foodAvatar: linkedFood?.images?.[0]?.image,
					amountText: foodHelper.getAmountText(food.foodAmount, linkedFood),
					cardImages:
						linkedFood?.images
							?.map((image) => {
								if (!image.image) {
									return undefined;
								}
								const cardImage: CardImage = {
									image: image.image,
									title: image.originalFilename
								};

								return cardImage;
							})
							.filter((m) => m)
							.map((m) => m as CardImage) ?? [],
					name: `${super.getRequiredItemName(linkedFood?.system.name)}`,
					linkedFood: linkedFood,
					category: linkedFood?.category,
					pieGraph: pieGraph,
					portionSelectionItems: portionSelectionItems
				};

				foodIndex++;

				return extendedFood;
			});

			const moveButtons: IButton[] = [];

			if (containers.length > 1) {
				if (this.canMoveDown(data.diet, containerIndex)) {
					moveButtons.push({
						buttonSize: 'default',
						buttonType: 'basic',
						iconOnly: true,
						title: `Move down`,
						click: () => {
							this.handleMove(data.diet, container, 'down');
						},
						icon: 'arrowDown'
					});
				}
				if (this.canMoveUp(data.diet, containerIndex)) {
					moveButtons.push({
						buttonSize: 'default',
						buttonType: 'basic',
						iconOnly: true,
						title: `Move up`,
						click: () => {
							this.handleMove(data.diet, container, 'up');
						},
						icon: 'arrowUp'
					});
				}
			}

			const extendedContainer: IExtendedDietFoodContainer = {
				originalContainer: container,
				domId: `dom_container_${container.id}`,
				shownTitle: signal(super.getRequiredItemName(container.title)),
				shownSubtitle: undefined,
				foods: extendedFoods,
				mealNutrition: foodHelper.calculateFoodNutrientsForContainer(
					container,
					linkedItemsHelper.getLinkedItemsByType<FoodDb.IFoodItem>(data.linkedItems, 'Food')
				),
				mealTypeButtons: this.getMealTypeButtons(container, containers),
				insideContainerButtons: [
					{
						buttonSize: 'default',
						buttonType: 'primaryLight',
						iconOnly: false,
						title: `Add food`,
						click: () => {
							this.openSelectFoodDialog(container);
						},
						icon: 'add'
					}
				],
				outsideContainerButtons: [
					...moveButtons,
					{
						buttonSize: 'default',
						buttonType: 'basic',
						iconOnly: true,
						title: `Remove meal`,
						click: () => {
							this.handleRemoveContainer(container);
						},
						icon: 'delete'
					}
				]
			};

			containerIndex++;

			return extendedContainer;
		});
	}

	private saveDiet(config?: IDietSaveConfig): void {
		const diet = this.diet();
		const sharedData = this.sharedData();
		const clientData = sharedData?.data.clientData;

		if (!diet || !sharedData) {
			return;
		}

		if (!clientData) {
			// save regular diet
			super.subscribeToObservable(
				this.dependencies.httpService
					.post<DietDb.IDietItem, Partial<DietDb.IDietItem>>((builder) =>
						builder.withCustomApiRoute('diet', 'edit').withData(diet).build()
					)
					.pipe(
						map((response) => {
							super.notifySaved('Diet');
							super.updateSharedData({
								linkedItems: this.combinedLinkedItems(),
								data: {
									diet: response.data,
									clientData: sharedData.data.clientData
								}
							});

							if (config?.scrollToContainerId) {
								this.scrollToContainer(config?.scrollToContainerId);
							}
						})
					)
			);
		} else {
			// save in client
			const dietContainers: readonly UserDb.IUserDietContainer[] = clientData.client.nutrition?.containers ?? [];

			const dietContainer = dietContainers.find((m) => m.id === clientData.dietContainer.id);

			if (!dietContainer) {
				throw new Error(`Invalid diet container with id '${clientData.dietContainer.id}'`);
			}

			const containerItemIndex = dietContainer.items.findIndex((m) => m.id === clientData.dietItem.id);

			// replace diet
			dietContainer.items[containerItemIndex].diet = diet;

			// save user
			super.subscribeToObservable(
				this.dependencies.httpService
					.post<UserDb.IUserItem, UserApi.IEditDietPlanRequest>((builder) =>
						builder
							.withCustomApiRoute('user', 'editUserDiet')
							.withId(clientData.client.id)
							.withData({
								title: clientData.client.nutrition?.title ?? 'default',
								dietContainers: dietContainers
							})
							.build()
					)
					.pipe(
						map((response) => {
							super.notifySaved('Diet');
							super.updateSharedData({
								linkedItems: this.combinedLinkedItems(),
								data: {
									diet: diet,
									clientData: sharedData?.data?.clientData
								}
							});

							if (config?.scrollToContainerId) {
								this.scrollToContainer(config?.scrollToContainerId);
							}
						})
					)
			);
		}
	}

	private scrollToContainer(containerId: string): void {
		const container = this.extendedDietFoodContainers()?.find((m) => m.originalContainer.id === containerId);

		if (container) {
			this.dependencies.windowScrollService.scrollToElement({
				elementId: container.domId
			});
		}
	}

	private initFoods(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<FoodDb.IFoodItem[]>((builder) => builder.withCustomApiRoute('food', 'custom').build())
				.pipe(
					map((response) => {
						this.customFoods.set(response.data);
					})
				)
		);
	}

	private getMealTypeButtons(
		container: DietDb.IDietFoodContainer,
		containers: DietDb.IDietFoodContainer[]
	): IButton[] {
		return [
			{
				title: 'Breakfast',
				click: () => {
					container.title = 'Breakfast';
					this.updateDiet();
				}
			},
			{
				title: 'Snack',
				click: () => {
					container.title = 'Snack';
					this.updateDiet();
				}
			},
			{
				title: 'Lunch',
				click: () => {
					container.title = 'Lunch';
					this.updateDiet();
				}
			},
			{
				title: 'Pre-workout meal',
				click: () => {
					container.title = 'Pre-workout meal';
					this.updateDiet();
				}
			},
			{
				title: 'Post-workout meal',
				click: () => {
					container.title = 'Post-workout meal';
					this.updateDiet();
				}
			},
			{
				title: 'Dinner',
				click: () => {
					container.title = 'Dinner';
					this.updateDiet();
				}
			},
			{
				title: 'Late snack',
				click: () => {
					container.title = 'Late snack';
					this.updateDiet();
				}
			}
		];
	}

	private openEditDialog(data: {
		readonly diet: DietDb.IDietItem;
		readonly getProp: (diet: DietDb.IDietItem) => string | undefined;
		readonly setProp: (diet: DietDb.IDietItem, value: string | undefined) => void;
		readonly title: string;
		readonly fieldName?: string;
		readonly fieldType: DialogFormFieldType;
	}): void {
		this.dependencies.dialogService.openFormDialog<EditTextDialog>({
			title: data.title,
			subtitle: data.diet.system.name,
			size: 'auto',
			fields: [
				{
					propertyName: 'text',
					required: false,
					title: data.fieldName,
					type: data.fieldType
				}
			],
			item: {
				text: data.getProp(data.diet)
			},
			save: (item) => {
				this.diet.update((currentDiet) => {
					if (currentDiet) {
						data.setProp(data.diet, item.text);
					}
					return cloneHelper.clone(currentDiet);
				});

				this._saveDiet.next({});
			}
		});
	}
}
